/** What every failed API call becomes. One shape, so screens never parse bodies themselves. */
export type ApiErrorData = Record<string, unknown>;

export class ApiError extends Error {
  readonly status: number;
  /** Stable machine code from the backend (`not_authenticated`, `stale_record_version`, ...).
   * `network_error` and `unknown_error` are ours, for failures with no backend answer. */
  readonly code: string;
  readonly detail: string;
  /** Extra keys the backend put beside `detail` and `code` (`findings`, `record_version`, ...). */
  readonly data: ApiErrorData;

  constructor(status: number, code: string, detail: string, data: ApiErrorData = {}) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.data = data;
  }

  get findings(): Finding[] {
    const f = this.data.findings;
    return Array.isArray(f) ? f.map(toFinding) : [];
  }
}

export type Finding = { code: string; detail: string; [extra: string]: unknown };

/** Findings are `{code, detail}` objects; send_blocked once returned bare codes, so accept both. */
function toFinding(f: unknown): Finding {
  if (typeof f === "string") return { code: f, detail: "" };
  const o = f as Record<string, unknown>;
  return { ...o, code: String(o.code ?? "unknown"), detail: String(o.detail ?? "") };
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Turn a non-2xx response into an ApiError. Handles the three shapes the backend produces:
 *  1. domain errors `{detail: string, code: string, ...extra}`
 *  2. framework validation errors `{detail: [{loc, msg, type}]}` (422, no `code`)
 *  3. bare `{detail: string}` (a proxy or the webhook 400) or a non-JSON body */
export async function decodeApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.clone().json();
  } catch {
    /* not JSON: fall through to a status-based message */
  }
  const status = response.status;
  if (body && typeof body === "object") {
    const { detail, code, ...rest } = body as Record<string, unknown>;
    if (Array.isArray(detail)) {
      return new ApiError(status, "validation_error", "Some fields are not valid.", {
        errors: detail,
      });
    }
    if (typeof code === "string") {
      return new ApiError(status, code, typeof detail === "string" ? detail : "", rest);
    }
    if (typeof detail === "string") return new ApiError(status, "unknown_error", detail, rest);
  }
  return new ApiError(status, "unknown_error", `The server answered ${status}.`);
}

export function networkError(cause: unknown): ApiError {
  const detail = cause instanceof Error ? cause.message : "The server could not be reached.";
  return new ApiError(0, "network_error", detail);
}

/** Field-level errors from a 422, keyed by the whole dotted path (`headcount.expected`). The last
 * part alone is ambiguous in a brief (`expected`, `total_cents`), so brief forms use this one. */
export function fieldErrorsByPath(e: ApiError): Record<string, string> {
  const errors = Array.isArray(e.data.errors)
    ? (e.data.errors as { loc?: unknown[]; msg?: string }[])
    : [];
  const out: Record<string, string> = {};
  for (const item of errors) {
    const path = (item.loc ?? []).map(String).join(".");
    if (path && item.msg && !(path in out)) out[path] = item.msg;
  }
  return out;
}

/** Field-level errors from a 422, keyed by the last part of the field path. */
export function fieldErrors(e: ApiError): Record<string, string> {
  const errors = Array.isArray(e.data.errors)
    ? (e.data.errors as { loc?: unknown[]; msg?: string }[])
    : [];
  const out: Record<string, string> = {};
  for (const item of errors) {
    const field = String(item.loc?.[item.loc.length - 1] ?? "");
    if (field && item.msg && !(field in out)) out[field] = item.msg;
  }
  return out;
}
